import { Avatar, colors } from "@mui/material";
import { GridApiCommon, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { createElement, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbCar, TbQuestionMark } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import MemoizedEditAutoComplete from "@/components/table/EditAutoCompletete";
import MemoizedEditableColumn from "@/components/table/EditableColumn";
import { stringToColor } from "@/helpers/strings";
import useDriversContext from "@/providers/data/DriversContext";
import { IHotelPlanningDepartureResponseItem, getHotelDepartureRowId } from "@/schemas/hotelPlanningDepartureSchema";
import { useHotelPlanningDepartureActions } from "@/stores/use-hotel-planning-departure-table";
import { DriverType } from "@/types/driver.types";

export type HotelDepartureAssignationFields = "assignedUser" | "labelledUser";
export const HotelDepartureAssignationColumn = ({
    t,
    drivers,
    fieldName,
}: {
    t: TFunction;
    drivers: DriverType[];
    fieldName: HotelDepartureAssignationFields;
}): GridColDef<IHotelPlanningDepartureResponseItem> => {
    return {
        field: fieldName,
        headerName: t(fieldName),
        minWidth: 220,
        editable: true,
        flex: 0.5,
        pastedValueParser: (value: string) => {
            const driver = drivers.find((item) => item.name === value || item.userId === value);
            return driver?.userId || undefined;
        },
        renderHeader: (params) => <TableHeader Component={TbCar} title={params.colDef.headerName!} />,
        valueGetter: (_val, row) => {
            return typeof row[fieldName] === "object" ? row[fieldName]?.userId : row[fieldName];
        },
        renderCell: (params: GridRenderCellParams<IHotelPlanningDepartureResponseItem>) => (
            <MemoComponent
                api={params.api}
                row={params.row}
                field={params.field as HotelDepartureAssignationFields}
                value={params.value}
            />
        ),
        renderEditCell: (params: GridRenderCellParams<IHotelPlanningDepartureResponseItem>) => (
            <MemoEditComponent
                api={params.api}
                row={params.row}
                field={params.field as HotelDepartureAssignationFields}
                value={params.value}
            />
        ),
    };
};

const MemoComponent = memo(
    ({
        value,
        row,
        field,
        api,
    }: {
        api: GridApiCommon;
        field: HotelDepartureAssignationFields;
        row: IHotelPlanningDepartureResponseItem;
        value: string;
    }) => {
        return (
            <MemoizedEditableColumn
                renderHistoryComp={(val) =>
                    val ? <HotelDepartureAssignationCell value={val as string} /> : <span>-</span>
                }
                api={api}
                field={field}
                id={getHotelDepartureRowId(row)}
                editable
            >
                <HotelDepartureAssignationCell value={value} />
            </MemoizedEditableColumn>
        );
    }
);

export const HotelDepartureAssignationCell = memo(({ value }: { value: string | number }) => {
    const { t } = useTranslation();
    const { drivers } = useDriversContext();
    const driver = useMemo(() => drivers.find((item) => item.userId == value), [drivers, value]);

    const color = stringToColor(driver?.name || "", -15);
    const info = useMemo<{
        color: string;
        icon: IconType;
        text: string;
        hasDriver?: boolean;
    }>(
        () =>
            driver
                ? {
                      color: `#${color}`,
                      icon: driver.icon || TbCar,
                      hasDriver: true,
                      text: driver.name,
                  }
                : {
                      color: colors.grey[500],
                      icon: TbQuestionMark,
                      text: "buttons.assignDriver",
                  },
        [color, driver]
    );

    return (
        <div className="flex items-center h-full gap-1    text-xs font-semibold" style={{ color: info.color }}>
            <Avatar sx={{ backgroundColor: info.color, color: "white", width: 25, height: 25 }} variant="rounded">
                {createElement(info.icon)}
            </Avatar>
            {info.hasDriver ? info.text : t(info.text)}
        </div>
    );
});

const MemoEditComponent = memo(
    ({
        value,
        row,
        field,
        api,
    }: {
        api: GridApiCommon;
        field: HotelDepartureAssignationFields;
        row: IHotelPlanningDepartureResponseItem;
        value: string;
    }) => {
        const { drivers } = useDriversContext();
        const { processRowUpdate } = useHotelPlanningDepartureActions();

        const val = (row[field] && row[field]?.userId) || value;
        const memozedDrivers = useMemo(
            () => drivers.map((item) => ({ label: item.name, value: item.userId })),
            [drivers]
        );
        const driver = useMemo(() => drivers.find((item) => item.name === val || item.userId === val), [drivers, val]);

        return (
            <MemoizedEditAutoComplete<IHotelPlanningDepartureResponseItem>
                processRowUpdate={processRowUpdate}
                api={api}
                row={row}
                field={field}
                value={driver?.userId || ""}
                options={memozedDrivers}
                optionComp={DriverOption}
                inputClassName="!h-[46px]"
                icon={TbCar}
                placeholder="driver"
                renderHistoryComp={(val) =>
                    val ? <HotelDepartureAssignationCell value={val as string} /> : <span>-</span>
                }
            />
        );
    }
);
function DriverOption({ option }: { option: { label: string; value: string | number } }) {
    return <HotelDepartureAssignationCell value={option.value} />;
}
